<!--DefaultLayout.vue-->
<template>
  <div class="flex flex-col min-h-screen">
    <Header/>
    <SearchComponent v-if="!$route.meta.hideSearch"/>

    <main class="flex-1 w-full flex flex-col">
      <router-view/>
    </main>

    <PerformerSelectedModal/>
    <Footer v-if="!$route.meta.fullscreen"/>

    <div v-if="!$route.meta.fullscreen" class="fixed bottom-0 left-0 right-0 bg-white dark:bg-gray-800 shadow-lg lg:hidden">
      <div class="flex justify-around items-center p-2">
        <router-link
          :to="routePath('FAVORITES', '/profile/favorites')"
          @click="handleFavoritesClick"
          class="flex flex-col items-center text-gray-600 dark:text-gray-400 hover:text-blue-500"
        >
          <FavoritesSvg/>
          <span class="text-xs">Избранное</span>
        </router-link>

        <router-link
          :to="routePath('SEARCH', '/search')"
          class="flex flex-col items-center text-gray-600 dark:text-gray-400 hover:text-blue-500"
        >
          <SearchSvg/>
          <span class="text-xs">Поиск</span>
        </router-link>

        <router-link
          :to="routePath('CREATE_AD', '/create-ad')"
          @click="handleCreateAdClick"
          class="flex flex-col items-center text-gray-600 dark:text-gray-400 hover:text-blue-500"
        >
          <CreateAdSvg/>
          <span class="text-xs wrap">Разместить</span>
          <span class="text-xs">объявление</span>
        </router-link>

        <router-link
          v-if="CHAT_ENABLED"
          :to="routePath('PROFILE_CHAT', '/profile/chat')"
          @click="handleChatClick"
          class="flex flex-col items-center text-gray-600 dark:text-gray-400 hover:text-blue-500"
        >
          <ChatIconWithBadge/>
          <span class="text-xs">Сообщения</span>
        </router-link>

        <router-link
          :to="routePath('PROFILE', '/profile')"
          @click="handleProfileClick"
          class="flex flex-col items-center text-gray-600 dark:text-gray-400 hover:text-blue-500"
        >
          <ProfileSvg/>
          <span class="text-xs">Профиль</span>
        </router-link>
      </div>
    </div>
  </div>
</template>

<script>
import Header from '@/core/components/material/v1/header/Header.vue';
import SearchComponent from '@/modules/search/components/SearchComponent.vue';
import Footer from '@/core/components/material/v1/footer/Footer.vue';
import ProfileSvg from "@/core/components/svg_components/ProfileSvg.vue";
import {useNavigation} from "@/router.js";
import SearchSvg from "@/core/components/svg_components/SearchSvg.vue";
import CreateAdSvg from "@/core/components/svg_components/CreateAdSvg.vue";
import FavoritesSvg from "@/core/components/svg_components/FavoritesSvg.vue";
import ChatIconWithBadge from "@/modules/profile/components/ChatIconWithBadge.vue";
import { CHAT_ENABLED } from '@/modules/profile/store/chatStore.js';
import PerformerSelectedModal from "@/modules/quick_post/components/PerformerSelectedModal.vue";
import {computed, onBeforeUnmount, onMounted, onUnmounted, ref, watch} from "vue";
import {useAuthStore} from "@/store/authStore.js";
import {useChatStore} from "@/modules/profile/store/chatStore.js";
import {usePerformerSelectionStore} from "@/modules/quick_post/store/performerSelectionStore.js";
import {HEADER_HEIGHT, HEADER_HEIGHT_MOBILE, SCREEN_HEIGHT_1024} from "@/constants.js";
import {useChat} from "@/modules/profile/composables/useChat.js";

export default {
  name: 'DefaultLayout',
  components: {
    PerformerSelectedModal,
    ChatIconWithBadge,
    FavoritesSvg,
    CreateAdSvg,
    SearchSvg,
    ProfileSvg,
    Header,
    SearchComponent,
    Footer,
  },
  setup() {
    const {routes, navigateTo} = useNavigation();
    const authStore = useAuthStore();
    const chatStore = useChatStore();
    const performerSelectionStore = usePerformerSelectionStore();
    const isMobile = ref(window.innerWidth < SCREEN_HEIGHT_1024);
    const offlineChecked = ref(false);
    const headerHeight = computed(() => (isMobile.value ? HEADER_HEIGHT_MOBILE : HEADER_HEIGHT));

    const chat = useChat({
      isMobile,
      headerHeight,
      showChatList: false,
      driveRouting: false,
    });

    const routePath = (routeKey, fallback) => routes?.[routeKey]?.path || fallback;

    async function handleOfflinePerformerCheck() {
      if (offlineChecked.value) return;
      offlineChecked.value = true;
      const items = await performerSelectionStore.loadOffline();
      if (items.length === 0) return;
      for (const item of items) {
        performerSelectionStore.enqueue(item);
      }
    }

    async function initializeRealtime() {
      if (!authStore.isAuthenticated) return;

      if (CHAT_ENABLED && !chatStore.initialized) {
        await chat.initChat();
      }
      if (chatStore.wsState === 'disconnected' || chatStore.wsState === 'error' || chatStore.wsState === 'disabled') {
        await chatStore.connectWebSocket();
      }
    }

    onMounted(async () => {
      try {
        await initializeRealtime();
        if (authStore.isAuthenticated) {
          handleOfflinePerformerCheck();
        }
      } catch (e) {
        console.error('DefaultLayout onMounted error:', e);
      }
    });

    onUnmounted(async () => {
      await chat.cleanup();
    });

    watch(
      () => authStore.isAuthenticated,
      (newVal) => {
        if (newVal) initializeRealtime();
        if (newVal) {
          handleOfflinePerformerCheck();
        }
        if (!newVal) {
          chat.cleanup();
          performerSelectionStore.clear();
          offlineChecked.value = false;
        }
      }
    );

    onBeforeUnmount(() => {
      try {
        if (authStore.isAuthenticated && chatStore.initialized) {
          chat.cleanup();
        }
      } catch (e) {
        console.error('DefaultLayout onBeforeUnmount error:', e);
      }
    });

    const handleFavoritesClick = () => {
      if (authStore.requireAuth()) {
        navigateTo(routes.FAVORITES);
      }
    };

    const handleCreateAdClick = () => {
      if (authStore.requireAuth()) {
        navigateTo(routes.CREATE_AD);
      }
    };

    const handleChatClick = () => {
      if (authStore.requireAuth()) {
        navigateTo(routes.PROFILE_CHAT);
      }
    };

    const handleProfileClick = () => {
      if (authStore.requireAuth()) {
        navigateTo(routes.PROFILE);
      }
    };

    return {
      routePath,
      handleFavoritesClick,
      handleCreateAdClick,
      handleChatClick,
      handleProfileClick,
      CHAT_ENABLED,
    };
  },
};
</script>

<style scoped>
</style>
